/**
 * @file index.tsx
 *
 * @fileoverview Component for displaying a grid with job teasers.
 */
import React from 'react';
import dynamic from 'next/dynamic';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { Badge } from '~/vcl/atoms/03-badge';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { HideDesktop } from '~/vcl/atoms/06-layout/02-visibility/01-hide-desktop';
import { Box } from '~/vcl/atoms/07-box';
import styles from './JobsTeaserGrid.module.scss';
import ShowAllJobsButton from './ShowAllJobsButton';
import { Job } from '~/jobiqo/types/types';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const BookmarkedJobsProvider = dynamic(
  import('~/jobiqo/components/BookmarkedJobs/context'),
);

const JobTeaserCard = dynamic(import('~/jobiqo/components/Job/Teaser'));

interface JobTeasersGridProps {
  /**
   * The list of jobs.
   */
  jobs: [Job];
  /**
   * The paragraph information.
   */
  paragraphInfo: any;
  /**
   * The size for the container.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
  /**
   * Query params to be added to the path.
   */
  showAllJobsQueryParams?: string;
}

export const JobTeasersGrid = ({
  jobs,
  paragraphInfo,
  maxContainerSize,
  showAllJobsQueryParams,
}: JobTeasersGridProps) => {
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);

  const ParagraphInfo = ({ children }) =>
    frontendTheme.LANDING_PAGES.JOB_TEASERS.INTRO_BADGE ? (
      <Badge>{children}</Badge>
    ) : (
      <Text
        className="jd-lp-hjob-teasers-intro"
        color={frontendTheme.LANDING_PAGES.JOB_TEASERS.INTRO_COLOR ?? 'primary'}
        fontWeight="bold"
        textTransform="uppercase"
      >
        {children}
      </Text>
    );

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      margin={0}
      padding={0}
      backgroundColorOverride={
        paragraphInfo.background
          ? `var(--color-${paragraphInfo.background})`
          : 'transparent'
      }
      data-test-id={paragraphInfo.bundle}
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <Spacing
          paddingTop={paragraphInfo.spacing ?? 11}
          paddingBottom={paragraphInfo.spacing ?? 12}
          margin={0}
        >
          <FlexItems justifyContent="space-between" alignItems="center">
            <FlexItem>
              {paragraphInfo.introText && (
                <>
                  <ParagraphInfo>{paragraphInfo.introText}</ParagraphInfo>
                  <Spacing size="medium" />
                </>
              )}
              {paragraphInfo.introTextSecondary && (
                <>
                  <Text
                    tag="h2"
                    fontSize={[5, 6]}
                    lineHeight={[5, 6]}
                    {...(frontendTheme.LANDING_PAGES.JOB_TEASERS
                      .INTRO_SECONDARY_COLOR
                      ? {
                          color:
                            frontendTheme.LANDING_PAGES.JOB_TEASERS
                              .INTRO_SECONDARY_COLOR,
                        }
                      : null)}
                  >
                    {paragraphInfo.introTextSecondary}
                  </Text>
                  <Spacing size="large" />
                </>
              )}
            </FlexItem>
            <HideMobile>
              <FlexItem justifySelf="flex-end">
                <ShowAllJobsButton
                  frontendTheme={frontendTheme}
                  queryParams={showAllJobsQueryParams}
                />
              </FlexItem>
            </HideMobile>
          </FlexItems>
          <Grid className={styles.jobsGrid}>
            {jobs?.length > 0 && (
              <BookmarkedJobsProvider
                initialState={jobs.filter((job) => job.bookmarked)}
              >
                {jobs.map((job) => (
                  <Cell display="grid" key={job.id}>
                    <JobTeaserCard job={job} />
                  </Cell>
                ))}
              </BookmarkedJobsProvider>
            )}
          </Grid>
          <HideDesktop>
            <Spacing />
            <ShowAllJobsButton
              frontendTheme={frontendTheme}
              queryParams={showAllJobsQueryParams}
            />
          </HideDesktop>
        </Spacing>
      </Main>
    </Box>
  );
};
